/**
 * How money reaches the workshop (#2467).
 *
 * Pricing answers "what do we charge"; this answers "how do we get paid".
 * They were interleaved before: the IBAN sat with the address, the VAT number
 * with the hourly rate, and payment terms were the last thing on the page,
 * three sections below the interest rate they belong with.
 *
 * The VAT *number* is here because it identifies the invoice. The VAT *rates*
 * stay in pricing because they are an input to price calculation.
 *
 * Payment terms are not part of `WorkshopSettings` — they live in the page's
 * own state and are passed through, which is why this section takes two extra
 * props rather than reading them off `settings`.
 */

import {
  Box,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { normalizeBic, normalizeIban } from '@/utils/iban';
import { formatDateTime } from '@/utils/locale';
import { PaymentTermsSettings } from '@/features/settings/PaymentTermsSettings';
import {
  CONSOLIDATED_INVOICING_CYCLES,
  biweeklyAnchorError,
  consolidationPreviewParams,
  formatConsolidationIssueDate,
  normalizeCutoffTime,
  type ConsolidatedInvoicingCycle,
  type ConsolidatedInvoicingFormValue,
} from '@/features/settings/consolidatedInvoicingForm';
import { useConsolidationSchedulePreview } from '@/hooks/useConsolidationSchedulePreview';

const WEEKDAY_LABEL_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
  paymentTermsDaysOptions: number[];
  onPaymentTermsChange: (value: number[]) => void;
  consolidatedInvoicing: ConsolidatedInvoicingFormValue;
  onConsolidatedInvoicingChange: (value: ConsolidatedInvoicingFormValue) => void;
}

function isCycle(value: string): value is ConsolidatedInvoicingCycle {
  return (CONSOLIDATED_INVOICING_CYCLES as readonly string[]).includes(value);
}

export function WorkshopSectionBilling({
  settings,
  onChange,
  paymentTermsDaysOptions,
  onPaymentTermsChange,
  consolidatedInvoicing,
  onConsolidatedInvoicingChange,
}: Props) {
  const { t } = useTranslation('settings');
  const previewParams = consolidationPreviewParams(consolidatedInvoicing);
  const preview = useConsolidationSchedulePreview(previewParams);
  const anchorError = biweeklyAnchorError(consolidatedInvoicing);

  const handleChange = (field: keyof WorkshopSettings, value: string | number) => {
    onChange({ ...settings, [field]: value });
  };

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('billingSection.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('billingSection.subtitle')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Grid container spacing={3}>
          {/*
            The IBAN blocks invoice finalization (finalize-draft-invoice.ts)
            but had no editor outside the first-run wizard, so a workshop that
            skipped it there could neither invoice nor fix the gap (#2461).
            Normalized on blur rather than on every keystroke, so typing a
            grouped IBAN stays comfortable.
          */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label={t('bankIban')}
              value={settings.bankIban ?? ''}
              onChange={(e) => handleChange('bankIban', e.target.value)}
              onBlur={(e) => handleChange('bankIban', normalizeIban(e.target.value))}
              helperText={t('bankIbanHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('bankBic')}
              value={settings.bankBic ?? ''}
              onChange={(e) => handleChange('bankBic', e.target.value)}
              onBlur={(e) => handleChange('bankBic', normalizeBic(e.target.value))}
              helperText={t('bankBicHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('vatNumber')}
              value={settings.vatNumber || ''}
              onChange={(e) => handleChange('vatNumber', e.target.value)}
              helperText={t('vatNumberHint')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('lateInterestRatePercent')}
              value={settings.lateInterestRatePercent ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onChange({ ...settings, lateInterestRatePercent: undefined });
                  return;
                }
                const val = parseFloat(raw);
                if (!Number.isNaN(val)) {
                  onChange({ ...settings, lateInterestRatePercent: val });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: { min: 0, max: 100, step: '0.1' },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('lateInterestRatePercentConsumer')}
              value={settings.lateInterestRatePercentConsumer ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onChange({ ...settings, lateInterestRatePercentConsumer: undefined });
                  return;
                }
                const val = parseFloat(raw);
                if (!Number.isNaN(val)) {
                  onChange({ ...settings, lateInterestRatePercentConsumer: val });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: { min: 0, max: 100, step: '0.1' },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">
              {t('lateInterestRatePercentHint')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <PaymentTermsSettings value={paymentTermsDaysOptions} onChange={onPaymentTermsChange} />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t('consolidatedInvoicing.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('consolidatedInvoicing.monthEndHelp')}
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={consolidatedInvoicing.enabled}
                onChange={(event) =>
                  onConsolidatedInvoicingChange({
                    ...consolidatedInvoicing,
                    enabled: event.target.checked,
                  })
                }
              />
            }
            label={t('consolidatedInvoicing.enabled')}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label={t('consolidatedInvoicing.cycle')}
                value={consolidatedInvoicing.cycle}
                onChange={(event) => {
                  const cycle = event.target.value;
                  if (!isCycle(cycle)) return;
                  onConsolidatedInvoicingChange({ ...consolidatedInvoicing, cycle });
                }}
              >
                {CONSOLIDATED_INVOICING_CYCLES.map((cycle) => (
                  <MenuItem key={cycle} value={cycle}>
                    {t(`consolidatedInvoicing.cycles.${cycle}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label={t('consolidatedInvoicing.weekday')}
                value={String(consolidatedInvoicing.weekday)}
                onChange={(event) => {
                  const weekday = Number(event.target.value);
                  if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) return;
                  onConsolidatedInvoicingChange({ ...consolidatedInvoicing, weekday });
                }}
              >
                {WEEKDAY_LABEL_KEYS.map((key, index) => (
                  <MenuItem key={key} value={String(index + 1)}>
                    {t(`days.${key}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="time"
                label={t('consolidatedInvoicing.cutoffTime')}
                value={consolidatedInvoicing.cutoffTime}
                onChange={(event) =>
                  onConsolidatedInvoicingChange({
                    ...consolidatedInvoicing,
                    cutoffTime: normalizeCutoffTime(event.target.value),
                  })
                }
                helperText={t('consolidatedInvoicing.cutoffTimeHint')}
                slotProps={{ htmlInput: { step: 60 } }}
              />
            </Grid>
            {consolidatedInvoicing.cycle === 'biweekly' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('consolidatedInvoicing.biweeklyAnchorWeek')}
                  value={consolidatedInvoicing.biweeklyAnchorWeek}
                  onChange={(event) =>
                    onConsolidatedInvoicingChange({
                      ...consolidatedInvoicing,
                      biweeklyAnchorWeek: event.target.value,
                    })
                  }
                  error={anchorError}
                  helperText={
                    anchorError
                      ? t('consolidatedInvoicing.anchorMustBeMonday')
                      : t('consolidatedInvoicing.anchorHint')
                  }
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 3 }} data-testid="consolidation-schedule-preview">
            <Typography variant="subtitle2" gutterBottom>
              {t('consolidatedInvoicing.previewTitle')}
            </Typography>
            {previewParams === null ? null : preview.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                {t('consolidatedInvoicing.previewLoading')}
              </Typography>
            ) : preview.isError ? (
              <Typography variant="body2" color="error">
                {t('consolidatedInvoicing.previewFailed')}
              </Typography>
            ) : (preview.data?.cutoffs.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('consolidatedInvoicing.previewEmpty')}
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {preview.data?.cutoffs.map((cutoff) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={`${cutoff.cutoffAt}-${cutoff.kind}-${cutoff.issueDate}`}
                  >
                    {formatDateTime(cutoff.cutoffAt)}
                    {' · '}
                    {t('consolidatedInvoicing.issueDate', {
                      date: formatConsolidationIssueDate(cutoff.issueDate),
                    })}
                    {cutoff.kind === 'month_end'
                      ? ` · ${t('consolidatedInvoicing.kind.month_end')}`
                      : ''}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
}
